import React, { useState, useRef, useEffect } from 'react';
import { DateRange, Calendar } from 'react-date-range';
import { format, parse, addDays } from 'date-fns';
import { vi } from 'date-fns/locale';
import { FaCalendarAlt, FaCalendarDay, FaTimes } from 'react-icons/fa';
import './styles.css';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

interface DatePickerCustomProps {
  label?: string;
  value?: string;
  onChange: (date: Date | null) => void;
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  disabled?: boolean;
  id?: string;
  selected?: Date | null;
  placeholderText?: string;
  dateFormat?: string;
}

const DatePickerCustom: React.FC<DatePickerCustomProps> = ({
  label,
  value,
  onChange,
  minDate,
  maxDate,
  className,
  disabled = false,
  id,
  selected,
  placeholderText = "Chọn ngày",
  dateFormat = "dd/MM/yyyy"
}) => {
  const [open, setOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  
  // Chuyển đổi chuỗi ngày thành đối tượng Date
  const parseDate = (dateString?: string | Date | any): Date => {
    if (!dateString) return new Date();
    
    // Nếu dateString đã là đối tượng Date, trả về nó
    if (dateString instanceof Date) {
      return dateString;
    }
    
    // Đảm bảo dateString là string
    if (typeof dateString !== 'string') {
      return new Date();
    }
    
    // Nếu dateString có định dạng ISO
    if (dateString.includes('T') || dateString.includes('-')) {
      try {
        // Xử lý chuỗi ngày tháng, đảm bảo format đúng
        if (dateString.includes('-')) {
          const [year, month, day] = dateString.split('-').map(Number);
          if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
            return new Date(year, month - 1, day, 12, 0, 0); // Thêm giờ là 12 để tránh vấn đề múi giờ
          }
        }
        
        // Tạo date với giờ là 12:00 để tránh vấn đề múi giờ
        const tempDate = new Date(dateString);
        return new Date(
          tempDate.getFullYear(), 
          tempDate.getMonth(), 
          tempDate.getDate(), 
          12, 0, 0
        );
      } catch (e) {
        console.error("Lỗi khi parse date:", e);
        return new Date();
      }
    }
    
    return new Date();
  };

  // Chuyển đổi đối tượng Date thành chuỗi ngày với định dạng YYYY-MM-DD
  const formatDate = (date: Date): string => {
    if (!date) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    return `${year}-${month}-${day}`;
  };

  const handleDateChange = (date: Date) => {
    // Đặt giờ là 12:00 để tránh vấn đề múi giờ
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      12, 0, 0
    );
    
    // Log để debug
    console.log('Date selected from Calendar:', date);
    console.log('Adjusted date with noon time:', selectedDate);
    
    onChange(selectedDate);
    setOpen(false);
  };

  // Xử lý khi người dùng nhấn nút "Hôm nay"
  const handleTodayClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const now = new Date();
    // Đặt giờ là 12:00 để tránh vấn đề múi giờ
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0);
    onChange(today);
  };

  const toggleCalendar = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const closeCalendar = () => {
    setOpen(false);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      calendarRef.current && 
      !calendarRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  const formatDisplayDate = (date?: Date | null): string => {
    if (!date) return '';
    return format(date, dateFormat);
  };

  // Thêm hàm để chuyển đổi labels tháng sang số
  const convertMonthLabelsToNumbers = () => {
    setTimeout(() => {
      const monthLabels = document.querySelectorAll('.rdrMonthName');
      monthLabels.forEach(label => {
        const text = label.textContent || '';
        // Chuyển đổi tên tháng sang số để dễ đọc hơn
        const monthNames = ['Tháng 1', 'Tháng 2', 'Tháng 3', 'Tháng 4', 'Tháng 5', 'Tháng 6', 
                           'Tháng 7', 'Tháng 8', 'Tháng 9', 'Tháng 10', 'Tháng 11', 'Tháng 12'];
        const monthIndex = monthNames.findIndex(name => text.includes(name));
        
        if (monthIndex !== -1) {
          const year = text.split(',')[1]?.trim() || '';
          label.textContent = `Tháng ${monthIndex + 1}, ${year}`;
        }
      });
    }, 0);
  };

  // Thêm hàm để sửa hiển thị ngày trong tuần
  const fixWeekdayDisplay = () => {
    setTimeout(() => {
      const weekDays = document.querySelectorAll('.rdrWeekDay');
      
      // Đảm bảo "CN" (Chủ nhật) hiển thị ở đầu
      if (weekDays.length === 7) {
        const parent = weekDays[0].parentElement;
        if (parent) {
          const cn = weekDays[6]; // Phần tử cuối là "CN"
          parent.insertBefore(cn, weekDays[0]);
        }
      }
    }, 0);
  };

  // Khởi tạo effect để xử lý click outside
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Thêm useEffect mới để chuyển đổi labels tháng sang số và sửa hiển thị ngày
  useEffect(() => {
    if (open) {
      // Chạy ngay lập tức và sau đó chạy lại để đảm bảo DOM đã được cập nhật
      convertMonthLabelsToNumbers();
      fixWeekdayDisplay();
      
      // Xử lý để đảm bảo date picker hiển thị đầy đủ trên thiết bị di động
      const handleResize = () => {
        if (window.innerWidth <= 768 && calendarRef.current) {
          // Đảm bảo calendar không bị che khuất bởi cạnh dưới màn hình
          const rect = calendarRef.current.getBoundingClientRect();
          const bottomSpace = window.innerHeight - rect.bottom;
          
          if (bottomSpace < 20) {
            // Điều chỉnh vị trí nếu calendar bị che khuất
            calendarRef.current.style.top = '40%';
            calendarRef.current.style.marginTop = '-160px';
          }
        }
      };
      
      handleResize();
      window.addEventListener('resize', handleResize);
      
      const timer = setTimeout(() => {
        convertMonthLabelsToNumbers();
        fixWeekdayDisplay();
        handleResize();
      }, 50);
      
      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [open]);

  // Xác định ngày đã chọn, ưu tiên sử dụng prop selected nếu có
  const selectedDate = selected || (value ? parseDate(value) : null) || null;
  
  return (
    <div className={`new-date-picker-wrapper ${open ? 'open' : ''}`} onClick={(e) => {
      // Đóng lịch khi click vào overlay (nền đen mờ)
      if ((e.target as HTMLElement).className === 'new-date-picker-wrapper open') {
        closeCalendar();
      }
    }}>
      {label && <label className="new-date-picker-label">{label}</label>}
      
      <div className="new-date-picker-input-container">
        <input
          id={id}
          type="text"
          className={`new-date-picker-input ${className || ''} ${disabled ? 'disabled' : ''}`}
          value={selectedDate ? formatDisplayDate(selectedDate) : ''}
          readOnly
          onClick={toggleCalendar}
          placeholder={placeholderText}
          disabled={disabled}
        />
        
        <div className="new-date-picker-icons" ref={buttonRef}>
          <button
            onClick={handleTodayClick}
            className="icon-button today-icon-button"
            title="Chọn ngày hôm nay"
            type="button"
            disabled={disabled}
          >
            <FaCalendarDay className="today-icon" />
          </button>
          <button
            onClick={toggleCalendar}
            className="icon-button calendar-icon-button"
            title="Mở lịch"
            type="button"
            disabled={disabled}
          >
            <FaCalendarAlt className="calendar-icon" />
          </button>
        </div>
      </div>
      
      {open && (
        <div className="new-date-picker-calendar-container" ref={calendarRef}>
          <button 
            className="calendar-close-button" 
            onClick={() => setOpen(false)}
            aria-label="Đóng lịch"
          >
            <FaTimes />
          </button>
          <Calendar
            date={selectedDate || new Date()}
            onChange={handleDateChange}
            locale={vi}
            color="#20757A"
            minDate={minDate}
            maxDate={maxDate}
            className="new-date-picker-calendar"
          />
        </div>
      )}
    </div>
  );
};

export default DatePickerCustom;